import { useEffect, useState, useCallback } from 'react'
import Cookies from 'js-cookie'

type ColorMode = 'light' | 'dark'

interface UseColorModeReturn {
  colorMode: ColorMode
  toggleColorMode: () => void
  setColorMode: (mode: ColorMode) => void
}

const COLOR_MODE_KEY = 'color-mode'

/**
 * Standalone color mode hook that doesn't depend on Chakra UI
 * Maintains compatibility with existing cookie-based storage
 */
export function useColorModeStandalone(): UseColorModeReturn {
  const [colorMode, setColorModeState] = useState<ColorMode>(() => {
    // Check cookie first (for SSR compatibility)
    const cookieValue = Cookies.get(COLOR_MODE_KEY)
    if (cookieValue === 'light' || cookieValue === 'dark') {
      return cookieValue
    }

    // Check localStorage
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(COLOR_MODE_KEY)
      if (stored === 'light' || stored === 'dark') {
        return stored
      }

      // Check system preference
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark'
      }
    }

    return 'light'
  })

  // Apply color mode class to document
  useEffect(() => {
    const root = document.documentElement

    if (colorMode === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }

    // Store in both cookie and localStorage for compatibility
    Cookies.set(COLOR_MODE_KEY, colorMode, {
      expires: 365,
      sameSite: 'lax',
    })

    if (typeof window !== 'undefined') {
      localStorage.setItem(COLOR_MODE_KEY, colorMode)
    }
  }, [colorMode])

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = (e: MediaQueryListEvent) => {
      // Only auto-change if no preference is stored
      const hasCookie = Cookies.get(COLOR_MODE_KEY)
      const hasLocalStorage = localStorage.getItem(COLOR_MODE_KEY)

      if (!hasCookie && !hasLocalStorage) {
        setColorModeState(e.matches ? 'dark' : 'light')
      }
    }

    mediaQuery.addEventListener('change', handleChange)

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  const setColorMode = useCallback((mode: ColorMode) => {
    setColorModeState(mode)
  }, [])

  const toggleColorMode = useCallback(() => {
    setColorModeState((prev: any) => (prev === 'light' ? 'dark' : 'light'))
  }, [])

  return {
    colorMode,
    toggleColorMode,
    setColorMode,
  }
}

/**
 * Hook to get color-mode aware values
 * Replaces Chakra's useColorModeValue
 */
export function useColorModeValue<TLight, TDark>(
  lightValue: TLight,
  darkValue: TDark
): TLight | TDark {
  const { colorMode } = useColorModeStandalone()
  return colorMode === 'light' ? lightValue : darkValue
}

/**
 * Hook to manage boolean state with helper functions
 * Replaces Chakra's useBoolean
 */
export function useBoolean(initialValue = false) {
  const [value, setValue] = useState(initialValue)

  const on = useCallback(() => {
    setValue(true)
  }, [])
  const off = useCallback(() => {
    setValue(false)
  }, [])
  const toggle = useCallback(() => {
    setValue((prev: any) => !prev)
  }, [])

  return [value, { on, off, toggle }] as const
}

/**
 * Hook to manage disclosure state
 * Replaces Chakra's useDisclosure
 */
export function useDisclosure(defaultIsOpen = false) {
  const [isOpen, setIsOpen] = useState(defaultIsOpen)

  const onOpen = useCallback(() => {
    setIsOpen(true)
  }, [])
  const onClose = useCallback(() => {
    setIsOpen(false)
  }, [])
  const onToggle = useCallback(() => {
    setIsOpen((prev: any) => !prev)
  }, [])
  const getButtonProps = useCallback(
    (props: any = {}) => ({
      ...props,
      'aria-expanded': isOpen,
      onClick: (event: any) => {
        props.onClick?.(event)
        onToggle()
      },
    }),
    [isOpen, onToggle]
  )
  const getDisclosureProps = useCallback(
    (props: any = {}) => ({
      ...props,
      hidden: !isOpen,
    }),
    [isOpen]
  )

  return {
    isOpen,
    onOpen,
    onClose,
    onToggle,
    getButtonProps,
    getDisclosureProps,
  }
}

/**
 * Hook to copy text to clipboard
 * Replaces Chakra's useClipboard
 */
export function useClipboard(text: string) {
  const [hasCopied, setHasCopied] = useState(false)

  const onCopy = useCallback(async () => {
    if (!text) return

    try {
      await navigator.clipboard.writeText(text)
      setHasCopied(true)

      // Reset after 2 seconds
      setTimeout(() => {
        setHasCopied(false)
      }, 2000)
    } catch (error) {
      console.error('Failed to copy text:', error)
      setHasCopied(false)
    }
  }, [text])

  return { value: text, onCopy, hasCopied }
}

/**
 * Hook for responsive breakpoint values
 * Replaces Chakra's useBreakpointValue
 */
export function useBreakpointValue<T>(
  values: {
    base?: T
    sm?: T
    md?: T
    lg?: T
    xl?: T
    '2xl'?: T
  },
  defaultValue?: T
): T | undefined {
  const [currentValue, setCurrentValue] = useState<T | undefined>(() => {
    if (typeof window === 'undefined') return defaultValue ?? values.base

    const width = window.innerWidth

    if (width >= 1536 && values['2xl'] !== undefined) return values['2xl']
    if (width >= 1280 && values.xl !== undefined) return values.xl
    if (width >= 1024 && values.lg !== undefined) return values.lg
    if (width >= 768 && values.md !== undefined) return values.md
    if (width >= 640 && values.sm !== undefined) return values.sm

    return values.base ?? defaultValue
  })

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth

      let newValue: T | undefined

      if (width >= 1536 && values['2xl'] !== undefined) {
        newValue = values['2xl']
      } else if (width >= 1280 && values.xl !== undefined) {
        newValue = values.xl
      } else if (width >= 1024 && values.lg !== undefined) {
        newValue = values.lg
      } else if (width >= 768 && values.md !== undefined) {
        newValue = values.md
      } else if (width >= 640 && values.sm !== undefined) {
        newValue = values.sm
      } else {
        newValue = values.base ?? defaultValue
      }

      setCurrentValue(newValue)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [values, defaultValue])

  return currentValue
}
